import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as StoreApi from "../api/storeApi";
import * as TranslationApi from "../api/translationApi";
import { isOk } from "../result";
import { StoreWriteModel, Store } from "../models/Store";
import { TranslationV2 } from "../models/Translation";

export const storesQueryKey = ["stores"] as const;

async function fetchStores(): Promise<ReadonlyArray<Store>> {
  const result = await StoreApi.list();
  if (!isOk(result)) {
    throw result.details;
  }
  return result.value;
}

export function useStoresQuery() {
  return useQuery({
    queryKey: storesQueryKey,
    queryFn: fetchStores,
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });
}

type UpsertStoreArgs = {
  storeId?: string;
  writeModel: StoreWriteModel;
  translation?: TranslationV2;
};

async function upsertStore({
  storeId,
  writeModel,
  translation
}: UpsertStoreArgs) {
  const result = storeId
    ? await StoreApi.update(storeId, writeModel)
    : await StoreApi.add(writeModel);

  if (!isOk(result)) {
    throw result.details;
  }

  if (translation?.values && Object.keys(translation.values).length > 0) {
    const tResult = await TranslationApi.upsert(translation);
    if (!isOk(tResult)) {
      throw tResult.details;
    }
  }
}

export function useUpsertStoreMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertStore,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: storesQueryKey });
      await queryClient.refetchQueries({
        queryKey: storesQueryKey,
        type: "active"
      });
    }
  });
}
